import React, { useState, useEffect } from 'react';
import { submitFormData } from '../../../../pages/api';

const JbAssembling = ({ formData = {}, onFormDataChange }) => {
    // Destructure formData to get initial values
    const {
        jbAssemblingObservations = [],
        abGlueObservations = [],
        nonConformance = '',
    } = formData;

    // State to hold table data (product, specifications, frequency)
    const [jbAssemblingData, setJbAssemblingData] = useState([]);
    const [abGlueData, setAbGlueData] = useState([]);

    // Fetch metadata and inspectionData only once on component mount
    useEffect(() => {
        const metadata = JSON.parse(sessionStorage.getItem('metadata'));
        const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData'));

        // Initialize table data from metadata
        if (metadata && metadata.post && metadata.post.JbAssemblingSoldering_post) {
            const { JbAssemblingSoldering, AbGlue } = metadata.post.JbAssemblingSoldering_post;
            setJbAssemblingData(JbAssemblingSoldering || []);
            setAbGlueData(AbGlue || []);
        }

        // Initialize observations and non-conformance from inspectionData only if formData is empty
        if (
            inspectionData &&
            inspectionData.PostLamination &&
            inspectionData.PostLamination.JbAssemblingSoldering_post &&
            (!jbAssemblingObservations.length &&
                !abGlueObservations.length &&
                !nonConformance.trim())
        ) {
            const { JbAssemblingSoldering, AbGlue, nonConformance: inspectionNonConformance } =
                inspectionData.PostLamination.JbAssemblingSoldering_post;

            // Map observation data from inspectionData
            const prefilledJbAssemblingObservations = JbAssemblingSoldering?.map((item) => ({
                observation1: item.observation1 || '',
                observation2: item.observation2 || '',
            })) || [];

            const prefilledAbGlueObservations = AbGlue?.map((item) => ({
                observation1: item.observation1 || '',
                observation2: item.observation2 || '',
            })) || [];

            // Update formData with observations and non-conformance
            onFormDataChange({
                ...formData,
                jbAssemblingObservations: prefilledJbAssemblingObservations,
                abGlueObservations: prefilledAbGlueObservations,
                nonConformance: inspectionNonConformance || '',
            });
        }
    }, []); // Empty dependency array ensures this runs only once on mount

    const handleObservationChange = (index, field, value, isJbAssembling) => {
        const fieldKey = isJbAssembling ? 'jbAssemblingObservations' : 'abGlueObservations';
        const updatedObservations = [...(formData[fieldKey] || [])];
        updatedObservations[index] = {
            ...updatedObservations[index],
            [field]: value,
        };
        onFormDataChange({ ...formData, [fieldKey]: updatedObservations });
    };

    const handleNonConformanceChange = (e) => {
        onFormDataChange({ ...formData, nonConformance: e.target.value });
    };

    const submitObservations = async () => {
        const projectId = localStorage.getItem('selectedProject');
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        const inspectionShift = sessionStorage.getItem('inspectionShift');
        const inspectorName = sessionStorage.getItem('username');
        const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');

        // Combine JB Assembling & Soldering data with observations
        const combinedJbAssemblingData = jbAssemblingData.map((item, index) => ({
            product: item.product || '',
            specifications: item.specifications || '',
            frequency: item.frequency || '',
            observation1: formData.jbAssemblingObservations?.[index]?.observation1 || '',
            observation2: formData.jbAssemblingObservations?.[index]?.observation2 || '',
        }));

        // Combine AB Glue data with observations
        const combinedAbGlueData = abGlueData.map((item, index) => ({
            product: item.product || '',
            specifications: item.specifications || '',
            frequency: item.frequency || '',
            observation1: formData.abGlueObservations?.[index]?.observation1 || '',
            observation2: formData.abGlueObservations?.[index]?.observation2 || '',
        }));

        const payload = {
            projectId,
            inspectionSupplier,
            inspectionDate,
            inspectionShift,
            inspectorName,
            status: 'completed',
            PostLamination: {
                JbAssemblingSoldering_post: {
                    JbAssemblingSoldering: combinedJbAssemblingData,
                    AbGlue: combinedAbGlueData,
                    nonConformance: formData.nonConformance || '',
                },
            },
        };

        try {
            await submitFormData(payload);
            alert('Observations submitted successfully');
        } catch (error) {
            console.error('Error submitting observations:', error);
            alert('Failed to submit observations');
        }
    };

    const renderTable = (title, data, fieldKey, isJbAssembling) => (
        <div className="mt-6">
            <h4 className="text-lg font-semibold mb-4 text-gray-700">{title}</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                    <thead>
                        <tr className="bg-[#424752] text-white text-center">
                            <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                            <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                            <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                            <th className="py-3 px-4 min-w-[200px] rounded-tr-lg">Observation 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item, index) => (
                            <tr
                                key={index}
                                className={`border-b last:border-none ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100 transition-colors duration-200`}
                            >
                                <td className="py-3 px-4">{item.product || ''}</td>
                                <td className="py-3 px-4">{item.specifications || ''}</td>
                                <td className="py-3 px-4">{item.frequency || ''}</td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={formData[fieldKey]?.[index]?.observation1 || ''}
                                        onChange={(e) => handleObservationChange(index, 'observation1', e.target.value, isJbAssembling)}
                                        placeholder="Enter observation 1"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={formData[fieldKey]?.[index]?.observation2 || ''}
                                        onChange={(e) => handleObservationChange(index, 'observation2', e.target.value, isJbAssembling)}
                                        placeholder="Enter observation 2"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">JB Assembling & Soldering Inspection</h3>

            {renderTable("JB Assembling & Soldering", jbAssemblingData, 'jbAssemblingObservations', true)}
            {renderTable("AB Glue", abGlueData, 'abGlueObservations', false)}

            <div className="mt-6">
                <h4 className="text-lg font-semibold mb-2 text-gray-700">Non Conformance</h4>
                <textarea
                    value={formData.nonConformance || ''}
                    onChange={handleNonConformanceChange}
                    placeholder="Enter details about non conformance..."
                    className="w-full border border-gray-300 p-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                    rows={4}
                />
            </div>

            <button
                onClick={submitObservations}
                className="mt-4 px-6 py-2 bg-[#424752] text-white rounded-lg hover:bg-gray-500 transition-colors duration-150"
            >
                Submit
            </button>
        </div>
    );
};

export default JbAssembling;